import { z } from "zod";
import {
  NESTED_CTA_TYPE_DISPLAY_MESSAGE,
  NESTED_CTA_TYPE_GO_TO_PAGE,
} from "@/types/nestedCtaType";
import {
  messagesArraySchema,
  optionalBoolean,
  optionalNumber,
  optionalString,
  parseWithRecord,
  stringFrom,
} from "./schemaPrimitives";

export const imageSchema = z.looseObject({
  url: stringFrom(""),
  altText: stringFrom(""),
  customImageUrl: optionalString.optional(),
  customImageCss: z
    .union([z.record(z.string(), z.union([z.string(), z.number()])), z.any()])
    .optional(),
});

export const textContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  text: stringFrom(""),
  formLabel: stringFrom(""),
  fontSize: optionalString.optional(),
});

export const contentFromSectionFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
});

export const imageContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  primary: optionalBoolean.optional(),
  image: z
    .union([imageSchema, z.any()])
    .transform((value) =>
      imageSchema.parse(
        typeof value === "object" && value !== null ? value : {},
      ),
    ),
});

export const personalizedImageContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  width: optionalNumber.optional(),
  image: z
    .union([imageSchema, z.any()])
    .transform((value) =>
      imageSchema.parse(
        typeof value === "object" && value !== null ? value : {},
      ),
    ),
});

export const columnsSectionSchema = z.object({
  name: stringFrom(""),
  label: stringFrom(""),
});

export const valuesSectionSchema = z.object({
  name: optionalString.optional(),
  origin: optionalString.optional(),
  facts: optionalString.optional(),
});

export const rowSectionSchema = z.object({
  conditionalOnVariable: optionalString.optional(),
  values: z
    .union([z.record(z.string(), z.unknown()), z.any()])
    .transform(
      (value): z.output<typeof valuesSectionSchema> =>
        parseWithRecord(value, valuesSectionSchema),
    ),
});

export const tableContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  columns: z
    .array(z.any())
    .catch([])
    .transform((arr) =>
      arr.map((item) => parseWithRecord(item, columnsSectionSchema)),
    ),
  columnNames: z
    .array(z.any())
    .catch([])
    .transform((arr) =>
      arr.map((item) => parseWithRecord(item, columnsSectionSchema)),
    ),
  rows: z
    .array(z.any())
    .catch([])
    .transform((arr) =>
      arr.map((item) => parseWithRecord(item, rowSectionSchema)),
    ),
});

export const chartContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  chartType: stringFrom(""),
  formLabel: stringFrom(""),
});

export const dataDistributionBackgroundSchema = z.looseObject({
  position: stringFrom(""),
  image: z
    .union([imageSchema, z.any()])
    .transform((value) =>
      imageSchema.parse(
        typeof value === "object" && value !== null ? value : {},
      ),
    ),
});

export const dataDistributionContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  valueFormatPattern: stringFrom(""),
  formLabel: stringFrom(""),
  label: z.any(),
  question: z.unknown().optional(),
  background: z.unknown().optional(),
});

const redirectToServiceSchema = z.object({
  name: stringFrom(""),
  specialization: stringFrom(""),
});

const redirectToServiceFieldSchema = z.preprocess(
  (val) =>
    val !== null && typeof val === "object" && !Array.isArray(val)
      ? val
      : undefined,
  z
    .union([redirectToServiceSchema, z.record(z.string(), z.string())])
    .optional(),
);

const onClickActionSchema = z.looseObject({
  type: z.string(),
  nextInsightId: optionalString.optional(),
});

export const buttonFieldsSchema = z.object({
  formLabel: stringFrom(""),
  key: stringFrom(""),
  text: stringFrom(""),
  contentFrom: optionalString.optional(),
  link: optionalString.optional(),
  redirectTo: optionalString.optional(),
  redirectToService: redirectToServiceFieldSchema,
  messages: messagesArraySchema.optional(),
  onClickAction: z
    .union([onClickActionSchema, z.record(z.string(), z.unknown())])
    .optional(),
});

export const buttonOverlayFieldsSchema = z.object({
  formLabel: stringFrom(""),
  key: stringFrom(""),
  text: stringFrom(""),
  contentFrom: optionalString.optional(),
  domain: optionalString.optional(),
  baseDir: optionalString.optional(),
  index: optionalString.optional(),
  year: optionalString.optional(),
});

export const nestedGoToPageCtaSchema = z.object({
  type: z.literal(NESTED_CTA_TYPE_GO_TO_PAGE),
  key: stringFrom(""),
  text: stringFrom(""),
  contentFrom: optionalString.optional(),
  link: optionalString.optional(),
  redirectTo: optionalString.optional(),
  redirectToService: redirectToServiceFieldSchema,
});

export const nestedDisplayMessageCtaSchema = z.object({
  type: z.literal(NESTED_CTA_TYPE_DISPLAY_MESSAGE),
  key: stringFrom(""),
  text: stringFrom(""),
  contentFrom: optionalString.optional(),
  messages: messagesArraySchema.optional(),
});

export const nestedCtaSchema = z.discriminatedUnion("type", [
  nestedGoToPageCtaSchema,
  nestedDisplayMessageCtaSchema,
]);

export const conditionalCtaOptionSchema = z.object({
  condition: stringFrom(""),
  cta: nestedCtaSchema,
});

export const conditionalCtaFieldsSchema = z.object({
  formLabel: stringFrom(""),
  options: z.array(z.any()).catch([]),
});

export const gridContentFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  nbColumns: optionalNumber.optional(),
  hideBorder: optionalBoolean.optional(),
  cells: z.array(z.any()).optional(),
  cell: z.any().optional(),
});

export const cellSectionFieldsSchema = z.object({
  conditionalOnVariable: optionalString.optional(),
  content: z.array(z.any()).catch([]),
});

export const monthOverviewFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  savingsVariationLink: stringFrom(""),
  debtVariationLink: stringFrom(""),
  formLabel: stringFrom(""),
});

export const monthOverviewDetailsFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  monthVariable: stringFrom(""),
  formLabel: stringFrom(""),
  paragraph1: z.any().optional(),
  paragraph2: z.any().optional(),
  paragraph3: z.any().optional(),
});

export const monthlyOverviewFieldsSchema = z.object({
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  moneyIn: z.any().optional(),
  moneyOut: z.any().optional(),
  creditCard: z.any().optional(),
  savings: z.any().optional(),
});

export const interactionBlueprintFieldsSchema = z.object({
  fileName: optionalString.optional(),
  metadata: z.any().optional(),
  content: z.any().optional(),
  advisorContent: z.any().optional(),
});
